import React, { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { widgetService } from '../../services';
import Button from '../ui/Button';
import {
  WidgetTemplate,
  WidgetType,
  SpecificWidgetConfig,
} from './types';
import { widgetRegistry, CREATABLE_WIDGET_DEFS } from './registry';
import { useZodForm } from '../../hooks/useZodForm';
import { widgetBuilderSchema, WidgetBuilderFormData } from '../../schemas';
import { getErrorMessage } from '../../utils/errors';
import { FALLBACK_WIDGET_ICON } from './widgetIcons';

interface WidgetBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  template?: WidgetTemplate | null;
  initialType?: WidgetType; // If provided, skip type selection and go directly to config
}

const getDefaultConfig = (type: WidgetType): SpecificWidgetConfig => {
  const def = widgetRegistry.get(type);
  if (!def) throw new Error(`Unknown widget type: ${type}`);
  return def.defaultConfig();
};

const WidgetBuilderModal: React.FC<WidgetBuilderModalProps> = ({
  isOpen,
  onClose,
  onSave,
  template,
  initialType,
}) => {
  const [step, setStep] = useState<'type' | 'config'>('type');

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting, isValid },
  } = useZodForm<WidgetBuilderFormData>(widgetBuilderSchema, {
    mode: 'onChange',
    defaultValues: { name: '', description: '', widgetType: null, config: null },
  });

  const widgetType = watch('widgetType') as WidgetType | null;
  const config = watch('config') as SpecificWidgetConfig | null;
  const name = watch('name');
  const description = watch('description');

  // Check if this is a system widget (read-only)
  const isSystemWidget = template && (widgetRegistry.get(template.type)?.meta.isSystem ?? false);

  useEffect(() => {
    if (template) {
      reset({
        name: template.name,
        description: template.description || '',
        widgetType: template.type,
        config: template.defaultConfig,
      });
      setStep('config');
    } else if (initialType) {
      reset({
        name: '',
        description: '',
        widgetType: initialType,
        config: getDefaultConfig(initialType),
      });
      setStep('config');
    } else {
      reset({ name: '', description: '', widgetType: null, config: null });
      setStep('type');
    }
  }, [template, isOpen, initialType, reset]);

  const handleTypeSelect = (type: WidgetType) => {
    setValue('widgetType', type, { shouldValidate: true });
    setValue('config', getDefaultConfig(type), { shouldValidate: true });
    setStep('config');
  };

  const handleBack = () => {
    if (!template && !initialType) {
      setStep('type');
    } else if (initialType) {
      onClose();
    }
  };

  const onSubmit = async (data: WidgetBuilderFormData) => {
    const wType = data.widgetType as WidgetType;
    const wConfig = data.config as SpecificWidgetConfig;
    const widgetName = data.name;

    try {
      clearErrors('root');

      if (template) {
        await widgetService.updateWidgetTemplate(template.id, {
          name: widgetName,
          description: data.description || undefined,
          defaultConfig: wConfig,
        });
      } else {
        await widgetService.createWidgetTemplate({
          name: widgetName,
          description: data.description || undefined,
          type: wType,
          defaultConfig: wConfig,
        });
      }

      onSave();
    } catch (err: unknown) {
      setError('root', { message: getErrorMessage(err, 'Failed to save widget template') });
    }
  };

  // Render the widget-specific builder using the registry
  const renderBuilder = () => {
    if (!widgetType || !config) return null;

    // Look up builder from registry
    const def = widgetRegistry.get(widgetType);
    if (!def) return null;

    const BuilderComponent = def.Builder;
    return (
      <Controller
        name="config"
        control={control}
        render={({ field }) => (
          <BuilderComponent
            config={field.value}
            onChange={(newConfig: SpecificWidgetConfig) => field.onChange(newConfig)}
          />
        )}
      />
    );
  };

  if (!isOpen) return null;

  const serverError = errors.root?.message;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {step === 'config' && !template && !initialType && (
                <button
                  onClick={handleBack}
                  className="text-neutral-500 hover:text-neutral-700"
                >
                  ← Back
                </button>
              )}
              <h2 className="text-xl font-semibold text-neutral-900">
                {isSystemWidget ? 'View System Widget' : template ? 'Edit Widget Template' : 'Create Widget Template'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {serverError && (
              <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
                {serverError}
              </div>
            )}

            {step === 'type' && (
              <div>
                <p className="text-neutral-600 mb-6">
                  Select the type of widget you want to create:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {CREATABLE_WIDGET_DEFS.map(({ type, meta }) => (
                    <button
                      key={type}
                      onClick={() => handleTypeSelect(type)}
                      className="p-4 border-2 border-neutral-200 rounded-lg text-left hover:border-primary-500 hover:bg-primary-50 transition-colors"
                    >
                      <span className="text-3xl">{meta.icon}</span>
                      <h3 className="font-medium text-neutral-900 mt-2">{meta.label}</h3>
                      <p className="text-sm text-neutral-500 mt-1">{meta.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 'config' && widgetType && config && (
              <div className="space-y-6">
                {/* System Widget Info Banner */}
                {isSystemWidget && (
                  <div className="bg-neutral-100 border border-neutral-300 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">
                        {widgetRegistry.get(widgetType)?.meta.icon || FALLBACK_WIDGET_ICON}
                      </span>
                      <div>
                        <h3 className="font-semibold text-neutral-900">{name}</h3>
                        <p className="text-sm text-neutral-600 mt-1">{description}</p>
                        <p className="text-xs text-neutral-500 mt-2 italic">
                          This is a system widget. Configuration cannot be modified.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Basic Info - Only show for non-system widgets */}
                {!isSystemWidget && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Widget Name *
                      </label>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            {...field}
                            placeholder="Enter widget name"
                            className={`w-full px-3 py-2 border rounded-lg ${
                              errors.name ? 'border-danger-400' : 'border-neutral-300'
                            }`}
                          />
                        )}
                      />
                      {errors.name && (
                        <p className="text-danger-600 text-xs mt-1">{errors.name.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Description *
                      </label>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            {...field}
                            placeholder="Brief description (required)"
                            className={`w-full px-3 py-2 border rounded-lg ${
                              errors.description ? 'border-danger-400' : 'border-neutral-300'
                            }`}
                          />
                        )}
                      />
                      {errors.description && (
                        <p className="text-danger-600 text-xs mt-1">{errors.description.message}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Widget-specific Configuration */}
                <div className={isSystemWidget ? '' : 'border-t pt-6'}>
                  <h3 className="text-lg font-medium text-neutral-900 mb-4">
                    {isSystemWidget ? 'Widget Details' : 'Widget Configuration'}
                  </h3>
                  {renderBuilder()}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {step === 'config' && (
            <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
              {isSystemWidget ? (
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-neutral-600 text-white rounded-lg hover:bg-neutral-700"
                >
                  Close
                </button>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isSubmitting || !isValid}
                  >
                    {isSubmitting ? 'Saving...' : template ? 'Update Widget' : 'Create Widget'}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetBuilderModal;
